import { Box, Button, Checkbox, CircularProgress, Collapse, Container, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, IconButton, Paper, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Student } from "../../../models/student";
import { Assessment, Item } from "../../../models/assessments";
import { ArrowBack, ArrowForward, Close, CloudUpload, Description, InfoOutlined } from "@mui/icons-material";
import { isRichTextContentEmpty } from "../../../utils/rich-text-empty";
import { RenderRichText } from "../../../components/render-rich-text";
import { useStudentActions } from "../../../hooks/useStudentActions";
import BackdropLoader from "../../../components/backdrop-loader";
import { PreStartAssessment } from "./components/pre-start-assessment";
import SubHeaderView from "./components/form/sub-header-view";
import TheoryView from "./components/form/theory-view";
import ObjectiveView from "./components/form/objective-view";
import FileUploadView from "./components/form/file-upload-view";
import ResourceDialog from "./components/resource-dialog";
import { useSnackbar } from "../../../components/snackbar";
import { uploadResource } from "../../../utils/upload-resource";
import sanitizeString from "../../../utils/sanitize-name";
import ConfirmSubmissionDialog from "./components/confirm-submission-dialog";
import axios from "axios";
import { shuffleArray } from "../../../utils/shuffle-array";
import SubmittedDialog from "./components/submitted-dialog";
import TimerView from "./components/timer-view";
import AccessExpired from "../../../components/access-expired";

const API_URL = process.env.REACT_APP_API_URL;

export default function AssessmentDetail() {
    const { classroom_id, assessment_id } = useParams<{classroom_id: string, assessment_id: string }>();

    const [loading, setLoading] = useState(true);

    const [uploadPercent, setUploadPercent] = useState<number | null>(null);

    const [submitted, setSubmitted] = useState(false);

    const {
        classrooms,
        getClassrooms,
        getAssessments,
        getAssessment,
        submitAssessment
    } = useStudentActions();

    const classroom = classrooms?.[Number(classroom_id)];
    const assessment = classroom?.assessments?.[Number(assessment_id)];

    const [locAssessment, setLocAssessment] = useState(assessment);

    const [questions, setQuestions] = useState<Item[][] | null>(null);

    const navigate = useNavigate();

    
    const [currentSection, setCurrentSection] = useState(0);

    const itemRefs = useRef<Record<string, () => Item>>({});

    const [resource, setResource] = useState<string>('');

    const [started, setStarted] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submissionDialog, setSubmissionDialog] = useState(false);
    const [submittedDialog, setSubmitedDialog] = useState(false);

    const autoSaveTimeout = useRef<NodeJS.Timeout | null>(null);


    const { showMessage } = useSnackbar();
    

    const handlePrevious = () => {
        setCurrentSection(currentSection - 1);
    };

    const handleNext = () => {
        setCurrentSection(currentSection + 1);
    };

    const debouncedAutoSave = () => {
        if (!locAssessment || !questions) return;

        if (autoSaveTimeout.current) clearTimeout(autoSaveTimeout.current);

        autoSaveTimeout.current = setTimeout(() => {
            const updatedQuestions: Item[][] = [];

            for (let sectionKey = 0; sectionKey < questions.length; sectionKey++) {
                let sectionQuestions = [];

                for (const item of questions[sectionKey]) {
                    if (itemRefs.current[item.id]) {
                        sectionQuestions.push(itemRefs.current[item.id]());
                    } else {
                        sectionQuestions.push(item);
                    }
                }

                updatedQuestions.push(sectionQuestions);
            }

            setQuestions(updatedQuestions)
            
            const responsesOnly = updatedQuestions.map((section) =>
                section.map((q) => ({
                    id: q.id,
                    answer: q.answer ?? null,
                }))
            );
            

            sessionStorage.setItem(
                `assessment-${locAssessment.id}-responses`,
                JSON.stringify(responsesOnly)
            );

        }, 300);
    };

    const handleSubmit = async () => {
        if (!questions || !questions || submitted) return;
        
        try {
            setSubmissionDialog(false);
            
            setSubmitting(true);
            const answers: any[][] = [];

            for (let curSection = 0; curSection < questions.length; curSection++) {
                const sectionAnswers = [];

                for (const question of questions[curSection]) {
                    if (question.type === 0) continue;

                    if (question.type === 5) {
                        if (question.answer) {
                            const path = `classrooms/${classroom_id}/assessments/${assessment_id}/submissions/${sanitizeString(question.answer.name)}`;                   
                        
                            await uploadResource(path, question.answer, question.answer.type, (percent) => {
                                setUploadPercent(percent);
                            });
                            
                            setUploadPercent(null);

                            sectionAnswers.push({ id: question.id, answer: path })
                        } else {
                            sectionAnswers.push({ id: question.id, answer: "" })
                        }
                        
                    } else {
                        sectionAnswers.push({ id: question.id, answer: question.answer })
                    }
                }

                answers.push(sectionAnswers);
            }

            await axios.put(
                `${API_URL}/student/classrooms/${classroom_id}/assessments/${assessment_id}/submit`,
                { "answer": answers },
                {  withCredentials: true }
            );

            submitAssessment(Number(classroom_id), Number(assessment_id));

            setSubmitedDialog(true);
            setSubmitted(true);
      
        } catch (error) {
            handleApiError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const backHome = () => {
        navigate(`/classrooms/${classroom_id}#assessments`);
    };

    const handleApiError = useCallback((error: any) => {
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    }, []);


    useEffect(() => {
        const loadClassrooms = async () => {
            if (!classrooms) await getClassrooms();
        }

        loadClassrooms();
    }, [classrooms, classroom_id]);

    useEffect(() => {
        const loadAssessments = async () => {
            if (classroom && !classroom.assessments) {
                await getAssessments(Number(classroom_id));
                await getAssessment(Number(classroom_id), Number(assessment_id));
            };
        }
        loadAssessments();
    }, [classroom, classroom_id, assessment_id]);

    useEffect(() => {
        if (assessment && assessment.instruction) setLocAssessment(assessment);
        setLoading(false);
    }, [assessment])
   
        
    if (!classroom || !locAssessment || loading) {
        return (<BackdropLoader/>)
    }

    if (classroom.expiry_date && new Date() >= new Date(classroom.expiry_date)) {
        return <AccessExpired classroom_id={classroom_id!}/>
    }


    if (!started) {
        return (
            <PreStartAssessment
                reg_number={classroom.reg_number}
                assessment={locAssessment}
                setStarted={setStarted}
                setQuestions={setQuestions}
            />
        );
    };

    if (!questions) {
        return <></>;
    }
    
    return (
        <Box sx={{ backgroundColor: "#c6e1f5", minHeight: "100vh" }}>
            <Box
                sx={{
                    position: "fixed",
                    width: '100%',
                    backgroundColor: "#1e293b",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Typography
                    sx={{
                        padding: 2,
                        color: "white",
                        fontWeight: 600,
                        fontSize: "1rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "70%",
                    }}
                >
                    {locAssessment.title}
                </Typography>
    
                {locAssessment.duration > 0 && (
                    <TimerView assessment={locAssessment} onTimeExpired={handleSubmit} started={started}/>
                )}
            </Box>
    
            <Container sx={{ paddingTop: 12, paddingBottom: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Section {currentSection + 1} of {questions.length}
                    </Typography>
                    <Box
                        sx={{
                            width: "100%",
                            height: 4,
                            backgroundColor: "#e2e8f0",
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                width: `${((currentSection + 1) / questions.length) * 100}%`,
                                height: "100%",
                                backgroundColor: "#3b82f6",
                                transition: "width 0.3s ease",
                            }}
                        />
                    </Box>
                </Box>
    
                <Box sx={{ mb: 4 }}>
                    {questions &&
                        (() => {
                            const sectionQuestions = questions[currentSection] || [];

                            return (
                                <Stack key={currentSection} spacing={2}>
                                    {sectionQuestions.map((question, index) => {
                                        const baseProps = {
                                            item: question,
                                            register: (id: string, getItem: () => Item) => {
                                                itemRefs.current[id] = getItem;
                                                debouncedAutoSave();
                                            },
                                            setResource: setResource
                                        };

                                        return (
                                            <React.Fragment key={question.id || index}>
                                                {question.type === 0 && (
                                                    <SubHeaderView setResource={setResource} item={question} />
                                                )}
                                                {(question.type === 1 || question.type === 2) && (
                                                    <TheoryView {...baseProps} />
                                                )}
                                                {(question.type === 3 || question.type === 4) && (
                                                    <ObjectiveView {...baseProps} />
                                                )}
                                                {question.type === 5 && (
                                                    <FileUploadView {...baseProps} />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Stack>
                            );
                        })()
                    }
                </Box>

    
     
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                >
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            onClick={handlePrevious}
                            disabled={currentSection === 0}
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderColor: "#616263",
                                color: "#374151",
                                '&:hover': {
                                    borderColor: "#9ca3af",
                                    backgroundColor: "#f9fafb",
                                },
                            }}
                        >
                            <ArrowBack sx={{ mr: 1 }} />
                            Prev
                        </Button>
    
                        <Button
                            variant="outlined"
                            onClick={handleNext}
                            disabled={currentSection === questions.length - 1}
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderColor: "#616263",
                                color: "#374151",
                                '&:hover': {
                                    borderColor: "#9ca3af",
                                    backgroundColor: "#f9fafb",
                                },
                            }}
                        >
                            Next
                            <ArrowForward sx={{ ml: 1 }} />
                        </Button>
                    </Box>
    
                    {(currentSection === questions.length - 1) && (
                        <Button
                            onClick={() => setSubmissionDialog(true)}
                            variant="contained"
                            sx={{
                                backgroundColor: '#059669',
                                textTransform: "none",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    backgroundColor: '#047857',
                                },
                                '&:disabled': {
                                    backgroundColor: '#9ca3af',
                                }
                            }}
                        >
                            {submitting ? <CircularProgress color="inherit" size={20}/> : "Submit"}
                        </Button>
                    )}
                </Box>
            </Container>
                    
            <ResourceDialog resource={resource} setResource={setResource}/>

            <ConfirmSubmissionDialog
                open={submissionDialog}
                setOpen={setSubmissionDialog}
                onConfirm={handleSubmit}
            />

            <SubmittedDialog
                open={submittedDialog}
                onClose={backHome}
                showScore={locAssessment.auto_marker && locAssessment.show_score}
            />

            { submitting && <BackdropLoader progress={uploadPercent}/>}

        </Box>
    );
}